/**
 * NotificationListener — pops a small, professional floating toast
 * whenever a NEW server notification arrives.
 *
 * Priority model (N1):
 *  • critical — duration Infinity, red left border, requires explicit "Got it"
 *  • high     — duration 10 000 ms
 *  • normal   — duration 7 000 ms
 *  • low      — duration 7 000 ms
 *
 * Login-flood guard:
 *  mountTimeRef records when the session started.  Any notification whose
 *  createdAt is ≤ that timestamp is pre-existing — shown in the bell panel
 *  only, never as a toast.
 */
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ChatTeardropDots,
  Briefcase,
  CheckCircle,
  ArrowsClockwise,
  Users,
  Gavel,
  FileText,
  CurrencyGbp,
  Bell,
  Clock,
  Warning,
  Scales,
  ClipboardText,
  CalendarCheck,
  XCircle,
  X,
} from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/features/auth/store';
import {
  useNotificationsStore,
  type Notification,
  type NotifType,
} from '@/lib/notificationsStore';
import { resolveNotificationLink } from '@/lib/notificationRouting';
import type { AuthUser } from '@/features/auth/types';

const ICON_BY_TYPE: Record<string, { Icon: React.ElementType; tone: string }> = {
  message_received:         { Icon: ChatTeardropDots, tone: 'text-brand-600 dark:text-brand-400 bg-brand-50 dark:bg-brand-500/15' },
  brief_received:           { Icon: Briefcase,        tone: 'text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/15' },
  brief_accepted:           { Icon: CheckCircle,      tone: 'text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-500/15' },
  brief_returned:           { Icon: ArrowsClockwise,  tone: 'text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-500/15' },
  brief_declined:           { Icon: ArrowsClockwise,  tone: 'text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-500/15' },
  brief_withdrawn:          { Icon: ArrowsClockwise,  tone: 'text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-500/15' },
  conference_requested:     { Icon: Users,            tone: 'text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-500/15' },
  conference_scheduled:     { Icon: CalendarCheck,    tone: 'text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-500/15' },
  conference_cancelled:     { Icon: XCircle,          tone: 'text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-500/15' },
  conference_completed:     { Icon: CheckCircle,      tone: 'text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-500/15' },
  hearing_booked:           { Icon: Gavel,            tone: 'text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/15' },
  hearing_assigned:         { Icon: Gavel,            tone: 'text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/15' },
  hearing_reminder:         { Icon: Clock,            tone: 'text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/15' },
  hearing_outcome_pending:  { Icon: Warning,          tone: 'text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/15' },
  case_assigned:            { Icon: Scales,           tone: 'text-brand-600 dark:text-brand-400 bg-brand-50 dark:bg-brand-500/15' },
  matter_inactive:          { Icon: Clock,            tone: 'text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-500/15' },
  conflict_check_required:  { Icon: Warning,          tone: 'text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-500/15' },
  task_assigned:            { Icon: ClipboardText,    tone: 'text-brand-600 dark:text-brand-400 bg-brand-50 dark:bg-brand-500/15' },
  task_due:                 { Icon: Clock,            tone: 'text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-500/15' },
  document_uploaded:        { Icon: FileText,         tone: 'text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-500/15' },
  fee_note_issued:          { Icon: CurrencyGbp,      tone: 'text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-500/15' },
  fee_note_paid:            { Icon: CurrencyGbp,      tone: 'text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-500/15' },
  fee_note_pending:         { Icon: CurrencyGbp,      tone: 'text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/15' },
  kyc_expiring:             { Icon: Warning,          tone: 'text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-500/15' },
  limitation_approaching:   { Icon: Warning,          tone: 'text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-500/15' },
  judgment_reserved:        { Icon: Gavel,            tone: 'text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-500/15' },
  clerk_coordination_requested: { Icon: Users,        tone: 'text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-500/15' },
  clerk_coordination_updated:   { Icon: CalendarCheck, tone: 'text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/15' },
  clerk_coordination_cancelled: { Icon: XCircle,      tone: 'text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-500/15' },
};

export function NotificationListener() {
  const user = useAuthStore((s) => s.user);
  const notifications = useNotificationsStore((s) => s.notifications);
  const markRead = useNotificationsStore((s) => s.markRead);
  const navigate = useNavigate();

  const mountTimeRef = useRef<number>(Date.now());
  const shownRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!user) return;

    for (const n of notifications) {
      if (shownRef.current.has(n.id)) continue;
      shownRef.current.add(n.id);
      if (n.read) continue;
      if (n.createdAt <= mountTimeRef.current) continue;

      const target = resolveNotificationLink(n, user);
      showToast(n, {
        onOpen: () => {
          markRead(n.id);
          if (target) navigate(target);
        },
        onAcknowledge: () => {
          markRead(n.id);
        },
      });
    }
  }, [notifications, user, markRead, navigate]);

  return null;
}

function showToast(
  n: Notification,
  { onOpen, onAcknowledge }: { onOpen: () => void; onAcknowledge: () => void },
) {
  const meta = ICON_BY_TYPE[n.type as NotifType] ?? { Icon: Bell, tone: 'text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-500/15' };
  const { Icon, tone } = meta;
  const isCritical = n.priority === 'critical';
  const isHigh = n.priority === 'high';

  toast.custom(
    (id) => (
      <div
        role="button"
        tabIndex={0}
        onClick={() => {
          if (!isCritical) {
            toast.dismiss(id);
            onOpen();
          }
        }}
        onKeyDown={(e) => {
          if (!isCritical && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            toast.dismiss(id);
            onOpen();
          }
        }}
        className={cn(
          'relative flex w-[360px] items-start gap-3 rounded-md border border-border/80 bg-card p-3.5 shadow-pop transition',
          isCritical
            ? 'border-l-[3px] border-l-red-500 cursor-default'
            : 'cursor-pointer hover:bg-muted/40',
        )}
      >
        {/* Close — dismiss the arriving toast without opening/navigating. */}
        <button
          onClick={(e) => { e.stopPropagation(); toast.dismiss(id); }}
          className="absolute right-1.5 top-1.5 rounded p-1 text-muted-foreground/50 transition hover:bg-muted hover:text-foreground"
          aria-label="Dismiss notification"
        >
          <X className="h-3.5 w-3.5" />
        </button>
        <div className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-md', tone)}>
          <Icon className="h-4 w-4" weight="duotone" />
        </div>
        <div className="min-w-0 flex-1 pr-4">
          <div className="text-sm font-semibold leading-snug text-foreground">
            {n.title}
          </div>
          {n.detail && (
            <div className="mt-1 line-clamp-2 text-xs leading-snug text-muted-foreground">
              {n.detail}
            </div>
          )}
          {isCritical ? (
            <div className="mt-2.5 flex gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toast.dismiss(id);
                  onAcknowledge();
                }}
                className="rounded border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 px-3 py-1 text-xs font-semibold text-red-700 dark:text-red-300 transition hover:bg-red-100 dark:hover:bg-red-500/20 dark:bg-red-500/15"
              >
                Got it
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toast.dismiss(id);
                  onOpen();
                }}
                className="rounded border border-border bg-card px-3 py-1 text-xs font-medium text-foreground transition hover:bg-muted"
              >
                Open →
              </button>
            </div>
          ) : (
            <div className="mt-1.5 text-xs font-medium uppercase tracking-[0.06em] text-brand-600 dark:text-brand-400">
              Click to open →
            </div>
          )}
        </div>
      </div>
    ),
    {
      duration: isCritical ? Infinity : isHigh ? 10_000 : 7_000,
      position: 'top-right',
    },
  );
}

export type { AuthUser };
